import {DefaultTFuncReturn} from "i18next";
import React, {ReactElement, useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {useAppSelector} from "app/store/hook";
import {useLocation} from "react-router-dom";
import {UserMenuModel} from "app/model/ua/user-menu";
import WeatherInfo from "../common/WeatherInfo";
import dayjs from 'dayjs';
import useMgLatestTime from "../../hooks/use-mgLatestTime-view";

type TimeFormat = 'YMD' | 'YM' | 'YMDH';
type MgMdmsType = 'K' | 'M' | 'KM';

interface ArcTitleProps {
  title: string | DefaultTFuncReturn;
  format?: TimeFormat;
  subTitle?: string;
  mgMdmsType?: MgMdmsType;
  etcLogo?: ReactElement;
}

export default function ArcTitle(props: Readonly<ArcTitleProps>) {
  const location = useLocation();
  const menuList: UserMenuModel[] = useAppSelector(state => state.menus.flatMenus);
  const [breadcrumb, setBreadcrumb] = useState<UserMenuModel[]>([]);
  const displayTimeFromHook = useMgLatestTime(props.mgMdmsType ?? 'KM');
  const displayTime = props.mgMdmsType ? displayTimeFromHook : dayjs();

  const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);
  const handleBreadcrumbsWhenResize = () => {
    setWindowWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', handleBreadcrumbsWhenResize);
    return () => {
      window.addEventListener('resize', handleBreadcrumbsWhenResize);
    }
  }, []);

  useEffect(() => {
    makeBreadcrumb();
  }, [location, menuList]);

  function makeBreadcrumb() {
    let breadArray: UserMenuModel[] = [];
    let currentMenuDepth: number = 0;
    const pathname: string = location.pathname;
    const currentMenu = menuList.find(menu =>
      menu.menuUrlAddr && pathname.startsWith(menu.menuUrlAddr));

    if (currentMenu) {
      currentMenuDepth = currentMenu.depth ?? 0;
      if (currentMenuDepth > 1) {
        breadArray.push(currentMenu);

        for (let i = 0; i < currentMenuDepth - 1; i++) {
          const upperMenuId = breadArray[0].uppoMenuId;
          if (upperMenuId) {
            const upperMenu = menuList.find(menu => menu.menuId === upperMenuId);
            if (upperMenu) {
              breadArray.unshift(upperMenu);
            }
          }
        }
      } else {
        breadArray.push(currentMenu);
      }

      if (currentMenu.menuUrlAddr !== pathname && props.title) {
        let menuName: string = props.title.toString();
        if (['detail', 'form', 'write-ask'].some(e => pathname.endsWith(e))) {
          if (pathname.endsWith('detail')) {
            menuName = '상세';
          } else if (pathname.endsWith('form')) {
            menuName = '등록';
          } else if (pathname.endsWith('write-ask')) {
            menuName = '답변';
          }

          breadArray.push({
            menuId: currentMenu.menuId + '-sub-menu',
            menuNm: menuName,
          });
        }
      }
    }

    setBreadcrumb(breadArray);
  }

  function convertTimeByFormat(format: TimeFormat, time: dayjs.Dayjs) {
    switch (format) {
      case 'YMDH': return time.format('YYYY.MM.DD HH:mm');
      case 'YMD' : return time.format('YYYY.MM.DD');
      case 'YM'  :
      default    : return time.format('YYYY.MM');
    }
  }

  return (
    <>
      <Helmet>
        <title>{props.title ?? 'e.CLOUD 플랫폼'}</title>
      </Helmet>
      <div className="page-tit">
        <h2 className="tit">
          {props.title}
          {(props.format && !props.mgMdmsType) && (
            <em className="badge">
              <i className="las la-clock" aria-hidden={true} />
              {convertTimeByFormat(props.format, dayjs())} 기준
            </em>
          )}
          {props.mgMdmsType && (
            <em className="badge">
              <i className="las la-clock" aria-hidden={true} />
              {convertTimeByFormat('YMDH', displayTime)} 기준
            </em>
          )}
          {props.subTitle && (
            <span>{props.subTitle}</span>
          )}
          {props.etcLogo && <>{props.etcLogo}</>}
        </h2>

        <div className="flexWrap4">
          <div className="breadcrumb">
            <ul>
              {breadcrumb.length > 0 && (
                <li className="home">
                  <a href="/#/db"><i className="xi-home" aria-hidden="true" /></a>
                </li>
              )}
              {breadcrumb?.map((bread, index) => {
                const lastIndex: number = breadcrumb.length - 1;
                let menuName: string = bread.menuNm ?? '';

                if (breadcrumb.length > 3 && index !== 0 && index !== lastIndex) {
                  const needShrink: boolean = windowWidth <= 600;
                  const needMoreShrink: boolean = windowWidth <= 400;

                  if ((needShrink && (index !== 0 && index !== 1 && index !== lastIndex))
                    || (needMoreShrink && index === 1 && index !== lastIndex)) {
                    menuName = '...';
                  }
                }

                return (
                <li key={bread.menuId} className={index === breadcrumb.length - 1 ? 'now flexWrap' : 'flexWrap'}>
                  <i className="xi-angle-right-min" aria-hidden="true" />
                  <a>{menuName}</a>
                </li>
                );
              })}
            </ul>
          </div>
          <WeatherInfo />
        </div>
      </div>
    </>
  );
}